import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* noop */ }
    try { localStorage.removeItem("evos_access_unlocked"); } catch (_) { /* noop */ }
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, refresh: checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Role helpers — rbac_role is one of: 'admin' | 'ops' | 'ro-viewer'
export const useRole = () => {
  const { user } = useContext(AuthContext);
  const role = user?.rbac_role || (user ? "ops" : "ro-viewer");
  return {
    role,
    isAdmin: role === "admin",
    isOps: role === "ops" || role === "admin",
    isReadOnly: role === "ro-viewer",
    canWrite: role === "ops" || role === "admin",
  };
};
